import React, { Component } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { cloneDeep } from 'lodash';
import { setTimeRange } from 'services/config/duck';
import { fetchEntityDataRange } from 'services/entities/duck';
import 'react-day-picker/lib/style.css';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { formatDate, parseDate } from 'react-day-picker/moment';
import { notify } from '@mrlannigan/reapop';
import breakpoint from 'styled-components-breakpoint';
import ButtonCompactPrimary from 'components/atoms/buttons/compact/Primary';
import TrackedComponentEnhancer from 'hoc/TrackedComponent';

const ButtonCompactPrimaryNoMinWidth = styled(ButtonCompactPrimary)`
    min-width: 0px;
`;
const TrackedComponentButton = TrackedComponentEnhancer({
    trackedName: 'Button-DatePicker-Go',
    targetComponent: ButtonCompactPrimaryNoMinWidth
});

const InputFromTo = styled.div`

    & .DayPicker-Day--selected:not(.DayPicker-Day--start):not(.DayPicker-Day--end):not(.DayPicker-Day--outside) {
        background-color: #fff !important;
        color: #000;
    }
    & .DayPicker-Day {
        border-radius: 0 !important;
    }
    & .DayPicker-Day--start {
        border-top-left-radius: 50% !important;
        border-bottom-left-radius: 50% !important;
    }
    & .DayPicker-Day--end {
        border-top-right-radius: 50% !important;
        border-bottom-right-radius: 50% !important;
    }
    .InputFromTo-to {
        margin-right: 4px;
    }

    .DayPicker-Caption {
        text-align: center;
    }

    .DayPicker-NavButton--prev {
        margin-right: 183px;

    }
    .DayPicker-NavButton {
        background-size: 70%;
    }

    .DayPickerInput-OverlayWrapper{
        position: absolute;
        top: 35px;
        left: -65px;

        ${breakpoint('xs', 'md')`
            top: 35px;
            left: 0px;
    `}
    }

    .DayPickerInput {
        font-size: 13px;
        margin-top: 10px;
    }

    .DayPickerInput input {
       padding: 8px 4px 4px;
       width: 100px;

       ${breakpoint('xs', 'md')`
            width: 85px;
            font-size: 11px;
    `}
    }

    .DayPickerInput input:focus {
        outline: 1px solid #FBAD19;
    }
    .DayPicker-Day--today {
        color: #FBAD19;
    }
    .dateInput {
        position: relative;
    }
    .dateInput label {
        position: absolute;
        left: 5px;
        padding: 0px 2px;
        font-size: 10px;
        color: #656158;
        background-color: #fff;
    }

    button {
        margin-left: 4px;
    }
`;

class DatePickerContainer extends Component {
    constructor(props) {
        super(props);
        this.handleFromChange = this.handleFromChange.bind(this);
        this.handleToChange = this.handleToChange.bind(this);
        this.applyDateRange = this.applyDateRange.bind(this);
        this.state = {
            from: new Date(this.props.selectedTimeRange.apiConfig.start().toISOString()),
            to: new Date(this.props.selectedTimeRange.apiConfig.end().toISOString())
        };
    }

    componentDidMount() {
        const { entityId } = this.props;
        this.props.fetchEntityDataRange({ entityId });
    }

    componentDidUpdate(prevProps) {
        const { entityId } = this.props;
        if (entityId !== prevProps.entityId) {
            this.props.fetchEntityDataRange({ entityId });
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);
    }

    componentWillReceiveProps(nextProps) {
        const state = this.state;
        state.from = new Date(nextProps.selectedTimeRange.apiConfig.start());
        state.to = new Date(nextProps.selectedTimeRange.apiConfig.end());
        this.setState(state);
    }

    focusTo() {
        // Focus to `to` field. A timeout is required here because the overlays
        // already set timeouts to work well with input fields
        this.timeout = setTimeout(() => this.toInput.getInput().focus(), 0);
    }

    showFromMonth() {
        const { from, to } = this.state;
        if (!from) {
            return;
        }
        if (moment(to).diff(moment(from), 'months') < 2) {
            this.toInput.getDayPicker().showMonth(from);
        }
    }

    handleFromChange(from) {
        if (this.state.from !== from) {
            this.setState({ from }, () => {
                if (from) {
                    this.focusTo();
                }
            });
        }
    }

    handleToChange(to) {
        this.setState({ to }, () => {
            this.showFromMonth();
        });
    }

    applyDateRange() {
        if (!this.state.from || !this.state.to) {
            if (!this.state.from) {
                this.props.notify({
                    title: 'Invalid Start Date Format',
                    message: 'Be sure that the start date is formatted MM-DD-YYYY',
                    status: 'error',
                    dismissible: true,
                    dismissAfter: 5000
                });
            }
            if (!this.state.to) {
                this.props.notify({
                    title: 'Invalid End Date Format',
                    message: 'Be sure that the end date is formatted MM-DD-YYYY',
                    status: 'error',
                    dismissible: true,
                    dismissAfter: 5000
                });
            }
        } else if (this.state.from > this.state.to) {
            this.props.notify({
                title: 'Invalid Date Range',
                message: 'Be sure that the "From" date is before the "To" date.',
                status: 'error',
                dismissible: true,
                dismissAfter: 5000
            });
        } else {
            const rangeObj = cloneDeep(this.props.timeRanges['custom-range']);
            const localFrom = new Date(this.state.from);
            const localTo = new Date(this.state.to);
            rangeObj.apiConfig.start = () => moment(localFrom.toISOString())
                .startOf('day').add(12, 'hours');
            rangeObj.apiConfig.end = () => moment(localTo.toISOString())
                .startOf('day').add(12, 'hours');
            this.props.setTimeRange(rangeObj);
        }
    }

    render() {
        const { from, to } = this.state;
        const { dataRange, pageRange = {} } = this.props;
        const today = moment().toDate();
        const modifiers = { start: from, end: to, highlighted: today };
        const disabledDays = { after: moment().subtract(1, 'days').toDate() };
        if (pageRange.start_date) {
            disabledDays.before = moment(pageRange.start_date).toDate();
        } else if (dataRange.start_date) {
            disabledDays.before = moment(dataRange.start_date).toDate();
        } else {
            disabledDays.before = moment('2016-01-01').toDate();
        }

        return (
            <InputFromTo className='InputFromTo'>
                <span className="InputFromTo-from dateInput">
                    <label>From</label>
                    <DayPickerInput
                        value={from}
                        placeholder='MM-DD-YYYY'
                        format="MM-DD-YYYY"
                        formatDate={formatDate}
                        parseDate={parseDate}
                        dayPickerProps={{
                            disabledDays,
                            modifiers,
                            month: from,
                            numberOfMonths: 1,
                        }}
                        onDayChange={this.handleFromChange}
                    /></span>{' '}
                <span id='fromToDivider'>-</span>{' '}
                <span className='InputFromTo-to dateInput'>
                    <label>To</label>
                    <DayPickerInput
                        ref={el => (this.toInput = el)}
                        value={to}
                        placeholder='MM-DD-YYYY'
                        format="MM-DD-YYYY"
                        formatDate={formatDate}
                        parseDate={parseDate}
                        dayPickerProps={{
                            disabledDays,
                            modifiers,
                            month: to,
                            numberOfMonths: 1,
                        }}
                        onDayChange={this.handleToChange}
                    />
                </span>
                <TrackedComponentButton
                    onClick={this.applyDateRange}
                    disabled={this.state.disableButton}
                >GO</TrackedComponentButton>
            </InputFromTo>
        );
    }
}

const mapStateToProps = state => ({
    selectedTimeRange: state.config.selectedTimeRange,
    timeRanges: state.config.timeRanges,
    dataRange: state.entities.dataRange
});

export default connect(mapStateToProps, {
    setTimeRange, notify, fetchEntityDataRange
})(DatePickerContainer);



// WEBPACK FOOTER //
// ./src/containers/DatePickerContainer.js